import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Tree } from 'ksnm-common-ui/lib/Tree';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { useCheckFromDictionaryCard } from '@src/utils/hooks/useCheckFromDictionaryCard';
import { usePermission } from '@src/utils/hooks/usePermission';
import { recursiveFindByCode } from '@src/utils/tree';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { indicatorsStore } from '@stores/dictionaryStores/indicatorsStore/indicatorsStore';
import { reportVersionsStore } from '@stores/dictionaryStores/reportVersion/reportVersionsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { useAuditView } from '../../hooks/useAuditView';
import { Filter } from './Filter';
import { Table } from './Table';
import { toTreeView } from './toTreeView';

import styles from './Indicators.module.scss';

const IndicatorsComponent = () => {
  const {
    fetch,
    uniqByVarCodeIndicators,
    filters,
    setBreadcrumbs,
    fullSize,
    selectNode,
    setSelectNode,
    selectedKeys,
    setSelectedKeys,
    expandedKeys,
    setExpandedKeys,
    clear,
  } = indicatorsStore;
  const { push } = useHistory();
  const { reportVersions } = reportVersionsStore;

  const permission = usePermission('CmIndicator');

  const isBack = useCheckFromDictionaryCard(RoutePath.DICTIONARY_INDICATORS);

  useAuditView('Показатели');

  useEffect(() => {
    fetch();
    fetchReferences(['reportGroups', 'reportVersions', 'filterOperators']);
  }, [fetch]);

  useEffect(() => {
    if (!isBack) clear();
  }, [clear, isBack]);

  const dataSource = useMemo(
    () =>
      uniqByVarCodeIndicators.map((item) => {
        const version = reportVersions?.find((v) => v.code === item.repVers);

        return {
          ...item,
          reportGroupCode: version?.reportGroupCode,
          reportGroupName: version?.reportGroupName,
          reportVersionName: version?.name,
          reportTypeCode: version?.reportTypeCode,
          reportTypeName: version?.reportTypeName,
          reportCodeExt: version?.reportCodeExt,
        };
      }),
    [uniqByVarCodeIndicators, reportVersions],
  );

  const treeData = useMemo(
    () =>
      toTreeView(
        dataSource.filter(
          (indicator) =>
            !(
              (filters.reportGroup && indicator.reportGroupCode !== filters.reportGroup) ||
              (filters.reportVersion && indicator.repVers !== filters.reportVersion)
            ),
        ),
      ),
    [filters.reportGroup, filters.reportVersion, dataSource],
  );

  const onSelect = useCallback(
    (_, { node, selected }) => {
      if (selected) {
        setSelectNode(node);
        setSelectedKeys([node.key]);

        const nodes = recursiveFindByCode(node.key, treeData);
        if (nodes) setBreadcrumbs(nodes.map((item) => item.title));
      }
    },
    [setBreadcrumbs, setSelectNode, setSelectedKeys, treeData],
  );

  const onExpand = useCallback(
    (keys) => {
      setExpandedKeys(keys);
    },
    [setExpandedKeys],
  );

  const onCreate = useCallback(() => {
    push(`${RoutePath.DICTIONARY_INDICATORS}/${NEW_ENTITY_ID}`);
  }, [push]);

  const actions = useMemo(
    () =>
      !selectNode && permission.CREATE ? (
        <Button onClick={onCreate} tooltip="Добавить">
          <Icon name="add" />
        </Button>
      ) : null,
    [onCreate, permission.CREATE, selectNode],
  );

  return (
    <DesignLayout scrollEnable>
      <Filter indicatorsWithGroup={dataSource} />

      <div className={styles.container}>
        <Tree
          selectable
          treeData={treeData}
          onSelect={onSelect}
          className={cm(styles.tree, { [styles.withTable]: selectNode, [styles.fullSize]: fullSize })}
          selectedKeys={selectedKeys}
          expandedKeys={expandedKeys}
          onExpand={onExpand}
          actions={actions}
        />
        <Table />
      </div>
    </DesignLayout>
  );
};

export const Indicators = observer(IndicatorsComponent);
